"use client";

import { useEffect, useRef, useState } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { postRequestCallback } from "@services/requestCallbackService";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import styles from "./AckoDriveStepsClientWrapper.module.scss";
import { getLoginUrl } from "@utils/getLoginUrl";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import SimpleLoader from "@components/atoms/SimpleLoader/SimpleLoader";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { useAppDispatch } from "@lib/redux/hooks";
import { updateProfileAsync } from "@lib/redux/slices/profileSlice";
import {
  CALLBACK_BUYING_PERIOD,
  prepareLeadDataForCapture,
  submitPreparedLeadForCapture,
} from "@helpers/leadCapture";

interface AckoDriveStepsClientWrapperProps {
  children: React.ReactNode;
  cityName?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
  pageType?: string;
  variantName?: string;
  isLoggedIn: boolean;
  segmentEventProps: any;
}

export default function AckoDriveStepsClientWrapper({
  children,
  cityName,
  brandName,
  modelName,
  carAvailability,
  pageType = "MM page",
  variantName,
  isLoggedIn,
  segmentEventProps,
}: AckoDriveStepsClientWrapperProps) {
  const [isCallBackModalOpen, setIsCallBackModalOpen] = useState(false);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const hasInitialized = useRef(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();

  const processLeadAndShowModal = async () => {
    // Prevent duplicate calls
    if (isLoadingLead || hasInitialized.current) {
      return;
    }
    
    setIsLoadingLead(true);
    hasInitialized.current = true;
    const buyingPeriod = CALLBACK_BUYING_PERIOD;
    const callbackTrackingProps = {
      car_availability: carAvailability,
      vehicle_make: brandName,
      vehicle_model: modelName,
      vehicle_variant: variantName,
      city_name: cityName,
      login_type: "Request callback",
      intent: buyingPeriod,
    };

    const profileUpdatePayload = {
      qualification_update_data: {
        buying_period: buyingPeriod,
      },
    };

    let leadData = null;

    try {
      try {
        leadData = await prepareLeadDataForCapture({
          pageType,
          carAvailability,
          brandName,
          modelName,
          variantName,
          cityName,
          vehicleColor: "",
          loginTypeLabel: "Request callback",
          componentName: "AckoDriveSteps",
          buyingPeriod,
          flowTypeLabel: "New Car",
        });
      } catch (error) {
        console.error("Lead preparation failed:", error);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType,
          additionalProps: {
            exception_type: "data_issue",
            exception_name: "lead preparation failed",
            ...callbackTrackingProps,
          },
        });
      }

      const [, callbackResult, profileResult] = await Promise.allSettled([
        leadData
          ? submitPreparedLeadForCapture({
              pageType,
              carAvailability,
              brandName,
              modelName,
              variantName,
              cityName,
              vehicleColor: "",
              leadData,
              buyingPeriod,
              loginTypeLabel: "Request callback",
              componentName: "AckoDriveSteps",
            })
          : Promise.resolve(null),
        postRequestCallback({
          callback_message: `Request callback for ${brandName} ${modelName} in ${cityName}`,
        }),
        dispatch(
          updateProfileAsync({
            profileUpdates: { buying_period: buyingPeriod },
            apiPayload: profileUpdatePayload,
          })
        ),
      ]);

      const callbackSucceeded =
        callbackResult.status === "fulfilled" &&
        callbackResult.value?.success;

      if (
        // @fix #001 - Invariant: callback success UI must depend on the callback request, not lead capture side effects.
        callbackSucceeded
      ) {
        setIsCallBackModalOpen(true);
      } else {
        // @fix #008 - Invariant: failed callback attempts must remain retryable.
        hasInitialized.current = false;
      }

      if (callbackResult.status === "fulfilled") {
        const callbackResponse = callbackResult.value;
        if (!callbackResponse.success) {
          console.error("Request callback failed:", callbackResponse.error);
          await trackEventWithCommonProps(
            "exception",
            EventType.TRACK_EVENT,
            {
              pageType,
              additionalProps: {
                exception_type: "API issue",
                exception_name: "failed callback request",
                ...callbackTrackingProps,
              },
            }
          );
        }
      } else {
        console.error("Callback API call failed:", callbackResult.reason);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "callback api call failed",
            ...callbackTrackingProps,
          },
        });
      }

      if (profileResult.status === "rejected") {
        console.error("Profile update promise failed:", profileResult.reason);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "profile update promise failed",
            ...callbackTrackingProps,
          },
        });
      } else if (updateProfileAsync.rejected.match(profileResult.value)) {
        console.error(
          "Profile update failed:",
          profileResult.value.payload || profileResult.value.error
        );
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "profile update failed",
            ...callbackTrackingProps,
          },
        });
      }
    } finally {
      setIsLoadingLead(false);
    }
  };

  const handleModalClose = () => {
    setIsCallBackModalOpen(false);
    const callbackReqParam = searchParams?.get("callbackReq");
    if(!callbackReqParam) return;
    
    // Clean up URL only
    const currentParams = searchParams ? searchParams.toString() : '';
    const newSearchParams = new URLSearchParams(currentParams);
    newSearchParams.delete('callbackReq');
    const newUrl = newSearchParams.toString() ? `${pathname}?${newSearchParams.toString()}` : pathname;
    router.replace(newUrl || "", { scroll: false });
  };

  useEffect(() => {
    const callbackReqParam = searchParams?.get("callbackReq");
    if (callbackReqParam === 'true' && isLoggedIn && !hasInitialized.current) {
      processLeadAndShowModal();
    }
  }, []); // Empty dependency array with ref protection

  const clickHandler = async () => {
    if (!isLoggedIn) {
      if (typeof window === "undefined") {
        return;
      }

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('callbackReq', 'true');
      const redirectUrl = currentUrl.toString();

      sessionStorage.setItem("loginType", "Request callback");
      sessionStorage.setItem("flowType", "New Car");
      localStorage.setItem("MMV", JSON.stringify({
        make: brandName,
        model: modelName,
        variant: variantName,
        color: "",
      }));

      await handleLoginFlow(getLoginUrl(redirectUrl), LoginSdkConfig.REQUEST_CALLBACK, redirectUrl);
      return;
    }

    processLeadAndShowModal();
  };

  return (
    <>
      <div className={styles.wrapper} onClick={clickHandler}>
        {children}
      </div>
      <RequestCallBackModal
        isOpen={isCallBackModalOpen}
        onClose={handleModalClose}
      />
     { isLoadingLead &&  <SimpleLoader
        isLoading={isLoadingLead}
        variant="spinner"
        size="medium"
        overlay={true}
        text="Processing your request..."
      />
     }
    </>
  );
} 
